import cloneDeep from "lodash/cloneDeep";
import uniqBy from "lodash/uniqBy";
import { Dimension, Quantity } from "dimtext";
import { updateItemCache } from "@local/lamina-core";

function scaledItems(
  grp,
  source,
  sourceOpeningLocation,
  target,
  targetOpeningLocation,
  scaleBy,
) {
  const itemsByOpening = grp.items.order.reduce((acc, id) => {
    const item = grp.items[id];
    if (source.drawing?.type !== "iiif" || target.drawing?.type !== "iiif")
      return [];

    if (!item.is_collection && item.collection_id) {
      if (!acc[item.collection_id]) {
        acc[item.collection_id] = [];
      }

      acc[item.collection_id].push(item);
    }

    return acc;
  }, {});

  const locsByRecord = grp.locations.order.reduce((acc, id) => {
    const loc = grp.locations[id];
    if (!acc[loc.record_id]) {
      acc[loc.record_id] = [];
    }

    acc[loc.record_id].push(loc);
    return acc;
  }, {});

  const minX = sourceOpeningLocation.clip.x;
  const minY = sourceOpeningLocation.clip.y;
  const maxX = minX + sourceOpeningLocation.clip.width;
  const maxY = minY + sourceOpeningLocation.clip.height;

  const sourceItems = itemsByOpening[source.id] || [];
  const sourceLocations = sourceItems
    .map((item) => locsByRecord[item.id] || [])
    .flat();

  const containedLocations = sourceLocations.filter((loc) => {
    if (!loc) return false;
    if (loc.page_id !== sourceOpeningLocation.page_id) return false;
    return (
      loc.position.x >= minX &&
      loc.position.x <= maxX &&
      loc.position.y >= minY &&
      loc.position.y <= maxY
    );
  });

  const usedItemIds = {};

  const targetData = containedLocations.map((loc) => {
    const item = grp.items[loc.record_id];
    const newItem = cloneDeep(item);

    if (usedItemIds[item.id]) {
      newItem.id = usedItemIds[item.id];
    } else {
      newItem.id = crypto.randomUUID();
      usedItemIds[item.id] = newItem.id;
    }

    newItem.collection_id = target.id;

    if (
      item.width &&
      source.width &&
      target.width &&
      ["horizontal", "both"].includes(scaleBy)
    ) {
      const w = item.cache.width_in;
      const ow = source.cache.width_in;
      const tw = target.cache.width_in;
      const scaleFactor = tw / ow;
      const nw = w * scaleFactor;
      newItem.width = new Dimension(new Quantity(nw, "inches"));
    }

    if (
      item.height &&
      source.height &&
      target.height &&
      ["vertical", "both"].includes(scaleBy)
    ) {
      const h = item.cache.height_in;
      const oh = source.cache.height_in;
      const th = target.cache.height_in;
      const scaleFactor = th / oh;
      const nh = h * scaleFactor;
      newItem.height = new Dimension(new Quantity(nh, "inches"));
    }

    updateItemCache(newItem, grp);

    return {
      item: newItem,
      old_id: item.id,
    };
  });

  if (!targetData.length) return [];

  let locCount = grp.locations.order.length;
  const newLocs = containedLocations.map((loc, i) => {
    const itemData = targetData[i];
    const newLoc = cloneDeep(loc);
    newLoc.id = crypto.randomUUID();
    newLoc.record_id = itemData.item.id;
    newLoc.index = locCount;
    locCount += 1;
    newLoc.page_id = targetOpeningLocation.page_id;
    const xpct =
      (loc.position.x - sourceOpeningLocation.clip.x) /
      sourceOpeningLocation.clip.width;
    const ypct =
      (loc.position.y - sourceOpeningLocation.clip.y) /
      sourceOpeningLocation.clip.height;
    newLoc.position.x =
      targetOpeningLocation.clip.x + xpct * targetOpeningLocation.clip.width;
    newLoc.position.y =
      targetOpeningLocation.clip.y + ypct * targetOpeningLocation.clip.height;
    return newLoc;
  });

  const updates = [
    {
      type: "item",
      action: "add",
      records: uniqBy(targetData, "old_id").map((d) => d.item),
      block: true,
    },
    {
      type: "location",
      action: "add",
      records: newLocs,
    },
  ];

  return updates;
}

export { scaledItems };
